import { offset, shift } from '@floating-ui/react-dom';
import { Check, MagnifyingGlass } from '@phosphor-icons/react';
import { ChevronDown, ChevronUp } from '@styled-icons/feather';
import classNames from 'classnames';
import { debounce } from 'lodash';
import React, {
  Dispatch,
  HTMLAttributes,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOutsideAlerter, useOutsideEvent } from '../../utils/eventAlerter';
import { Floating } from '../Floating';
import { TabContent, TabList, TabRoot, TabTrigger } from '../TabsSolid';
import styles from './SelectFont.module.css';

interface SelectFontContextInterface {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const SelectFontContext = createContext<SelectFontContextInterface>({
  isOpen: false,
  setIsOpen: () => {
    throw Error('You forgot to wrap this component around SelectFontContext Provider');
  },
});

type SelectFontProps = PropsWithChildren<{
  disabled?: boolean;
  triggerLabel: React.ReactNode;
  onSearch?: (val: string) => void;
  usedFonts?: React.ReactNode;
}>;

export function SelectFont({
  disabled = false,
  triggerLabel,
  onSearch = () => null,
  children,
  usedFonts = null,
}: SelectFontProps) {
  const [isOpen, setIsOpen] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useOutsideAlerter(ref, '', () => setIsOpen(false));

  const floatingRef = useRef<Floating>(null);

  const buttonRef = useOutsideEvent<HTMLButtonElement>('mousedown', (event) => {
    if (
      floatingRef.current?.container === event.target ||
      floatingRef.current?.container?.contains(event.target as Node)
    ) {
      return;
    }
    setIsOpen(false);
  });

  const [searchKeyword, setSearchKeyword] = useState('');
  const debouncedSearch = useMemo(() => debounce(onSearch, 200), []);

  useEffect(() => {
    debouncedSearch(searchKeyword);

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchKeyword]);

  const ChevronIcon = isOpen ? ChevronUp : ChevronDown;

  return (
    <SelectFontContext.Provider value={useMemo(() => ({ isOpen, setIsOpen }), [isOpen])}>
      <div ref={ref}>
        <button
          ref={buttonRef}
          type="button"
          className={classNames(styles.selectFontTrigger, styles.fullWidth, {
            [styles.selectFontTriggerDisabled]: disabled,
          })}
          aria-haspopup="true"
          aria-controls="select-font-menu"
          onClick={() => !disabled && setIsOpen((curr) => !curr)}
        >
          {!disabled ? (
            <div className="is-flex is-align-items-center">
              <div className="is-flex-grow-1">{triggerLabel || ''}</div>
              <ChevronIcon size={16} className="ml-1" />
            </div>
          ) : (
            triggerLabel
          )}
        </button>
        <Floating
          ref={floatingRef}
          show={isOpen}
          reference={buttonRef}
          options={{
            strategy: 'fixed',
            placement: 'left-start',
            middleware: [
              offset({ mainAxis: 32, crossAxis: -64 }),
              shift({ crossAxis: true, padding: { top: 8, bottom: 8 } }),
            ],
          }}
          autoUpdate
        >
          <div
            className={classNames(
              styles.selectFontMenu,
              'bg-background dark:bg-background-secondary'
            )}
            id="select-font-menu"
            role="menu"
          >
            <TabRoot defaultValue="all">
              <h1 className={styles.selectFontMenuHeading}>Fonts</h1>
              <TabList style={{ marginBottom: 24 }}>
                <TabTrigger value="all">All</TabTrigger>
                <TabTrigger value="in_this_page">In this page</TabTrigger>
              </TabList>
              <div className={classNames(styles.selectFontMenuSearch, 'bg-background-secondary')}>
                <MagnifyingGlass
                  className={classNames(styles.selectFontMenuSearchIcon, 'text-primary-soft')}
                  size={20}
                />
                <input
                  type="input"
                  className={classNames(
                    styles.selectFontMenuSearchInput,
                    'bg-background-secondary dark:bg-background-tertiary text-primary placeholder:text-primary-soft'
                  )}
                  placeholder="Search fonts..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </div>
              <TabContent value="all">
                <div className={styles.selectFontMenuList}>{children}</div>
              </TabContent>
              <TabContent value="in_this_page">
                <div className={styles.selectFontMenuList}>{usedFonts}</div>
              </TabContent>
            </TabRoot>
          </div>
        </Floating>
      </div>
    </SelectFontContext.Provider>
  );
}

SelectFont.defaultProps = {
  disabled: undefined,
  onSearch: undefined,
};

type SidePopupItemProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
};

export function SelectFontItem({
  active,
  className,
  onClick = () => null,
  onKeyDown: _,
  children,
  ...props
}: SidePopupItemProps) {
  const { setIsOpen } = useContext(SelectFontContext);

  const handleClick = (e: any) => {
    onClick(e);
    setIsOpen(false);
  };

  return (
    <div
      className={classNames(className, styles.selectFontMenuItem, {
        [styles.selectFontMenuItemActive]: active,
      })}
      onClick={handleClick}
      onKeyDown={handleClick}
      role="button"
      tabIndex={0}
      {...props}
    >
      <span>{children}</span>
      {active ? <Check weight="bold" size={16} /> : null}
    </div>
  );
}
